import cx from "classnames";

import { Feature, IPaginator } from "common/types";

import Pagination from "client/common/components/layouts/Pagination";
import { useBreakpoints } from "client/common/hooks";
import { useFeatures } from "client/clinics/hooks";
import { ProductListSource } from "client/analytics/types";
import {
  ProductListContext,
  useProductsViewOnVisible,
  useViewItemListOnVisible,
} from "client/analytics/utils";

import { ProductSearchResult, ProductSearchResultV2 } from "../types";
import ProductCard from "./ProductCard";
import ProductCardV2 from "./ProductCardV2";
import ProductCardMobile from "./ProductCardMobile";
import ProductCardMobileV2 from "./ProductCardMobileV2";

import styles from "../styles/ProductCardList.module.less";

type ProductCardListProps = {
  results: ProductSearchResult[];
  listSource: ProductListSource;
  paginator: IPaginator;
  onPageChange: (page: number) => void;
  entityName: string;
  listContext?: ProductListContext;
  className?: string;
};

/**
 * A page of product cards and its pagination: a grid on desktop, a stacked list on mobile. Reports
 * its own impressions, so callers only supply the data and the list's analytics source.
 */
const ProductCardList = ({
  results,
  listSource,
  paginator,
  onPageChange,
  entityName,
  listContext,
  className,
}: ProductCardListProps) => {
  const isV2 = useFeatures()[Feature.ProductSearchCardsV2];
  const { isTabletPortraitAndSmaller } = useBreakpoints();
  const itemListRef = useViewItemListOnVisible(results, listSource);
  const productsViewRef = useProductsViewOnVisible(results, listSource, listContext);

  // Safe because the same flag routed the query to the v2 endpoint.
  const v2Results = results as ProductSearchResultV2[];

  const renderCards = () => {
    if (isTabletPortraitAndSmaller) {
      const MobileCard = isV2 ? ProductCardMobileV2 : ProductCardMobile;

      return (
        <div ref={productsViewRef} className={styles.list}>
          {v2Results.map((result) => (
            <MobileCard
              key={result.id}
              result={result}
              listSource={listSource}
              collectionSlug={listContext?.collectionSlug}
            />
          ))}
        </div>
      );
    }

    const DesktopCard = isV2 ? ProductCardV2 : ProductCard;

    return (
      <div ref={productsViewRef} className={cx(styles.grid, isV2 && styles.gridV2)}>
        {v2Results.map((result) => (
          <DesktopCard
            key={result.id}
            result={result}
            listSource={listSource}
            collectionSlug={listContext?.collectionSlug}
          />
        ))}
      </div>
    );
  };

  return (
    <div ref={itemListRef} className={cx(styles.section, className)}>
      {renderCards()}
      <Pagination paginator={paginator} onPageChange={onPageChange} entityName={entityName} />
    </div>
  );
};

export default ProductCardList;
